import { Injectable, Inject, REQUEST, TransferState, Optional, makeStateKey, ApplicationRef } from "@angular/core";
import { SsrService } from "./ssr.service";
import { LogginService } from "./logging/logging-provider";
import pino from "pino";
// import { Breakpoints } from "@hrra/common-models";



export class Breakpoints {
  static small = 320;
  static medium = 768;
  static large = 1024;
  static huge = 1344;
}


export interface DeviceInfo {
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceDetectionService {

  private hydrationComplete = false;
  private ssrEnabled = true;
  private logger: pino.Logger;

  public init(ssrEnabled = true) {
    this.ssrEnabled = ssrEnabled;
    this.appRef.whenStable().then(() => {
      this.hydrationComplete = true;
    });
  }

  private isCloudFrontDeviceHeaderPresent(headers: Headers) {
    return headers.has('cloudfront-is-mobile-viewer') ||
      headers.has('cloudfront-is-desktop-viewer') ||
      headers.has('cloudfront-is-tablet-viewer');
  }

  private detetctByWindowSize(): DeviceInfo {
    const result = {
      isMobile: false,
      isDesktop: false,
      isTablet: false
    };

    result.isMobile = window.innerWidth < Breakpoints.medium;
    result.isDesktop = window.innerWidth >= Breakpoints.huge;
    result.isTablet = window.innerWidth >= Breakpoints.medium && window.innerWidth < Breakpoints.huge;

    return result;

  }

  private detectByHeaders(){
    const result = {
      isMobile: false,
      isDesktop: false,
      isTablet: false
    };

    result.isMobile = this.request.headers.get('cloudfront-is-mobile-viewer') == 'true';
    result.isDesktop = this.request.headers.get('cloudfront-is-desktop-viewer') == 'true';
    result.isTablet = this.request.headers.get('cloudfront-is-tablet-viewer') == 'true';

    return result;

  }


  detectDevice(): DeviceInfo {
    let result = {
      isMobile: false,
      isDesktop: false,
      isTablet: false
    };

    this.logger.trace('Detecting device type...');

    const key = makeStateKey<DeviceInfo>('device-info');

    if (!this.ssrEnabled) {

      if(this.ssrService.isPlatformServer()){
        this.logger.error('Window detection is performed on server, incorrect usage of DeviceDetectionService. (ssrEnabled is false)');
      }
      else{
        this.logger.debug('Ssr not enabled, using window size detection.');
      }

      result = this.detetctByWindowSize();
    }
    else if (this.ssrService.isPlatformBrowser() && this.hydrationComplete) {
      this.logger.debug('Device detection: Using window size detection in browser after hydration.');
      result = this.detetctByWindowSize();
    }
    else if (this.ssrService.isPlatformBrowser() && !this.hydrationComplete) {
      this.logger.debug('Client device detection: Using server provided values as hydration is not complete.');
      if (this.transferState.hasKey(key)) {
        const deviceInfo = this.transferState.get<DeviceInfo>(key, null);
        result = deviceInfo;
        this.logger.debug(result, 'Device detection complete');
      }
      else {
        this.logger.error('Device detection failed: No device info found in transfer state.');
      }
    }
    else if (this.ssrService.isPlatformServer() && this.request !== null && this.isCloudFrontDeviceHeaderPresent(this.request.headers)) {
      this.logger.debug('Device detection: Using headers from request in server.');
      result = this.detectByHeaders();
      this.logger.debug(result, 'Server device detection complete');
      this.transferState.set(key, result);
    }
    else if (this.ssrService.isPlatformServer() && this.request !== null && !this.isCloudFrontDeviceHeaderPresent(this.request.headers)) {
      //switch to agent detection
      this.logger.error('No cloudfront headers found');
    }
    else if (this.ssrService.isPlatformServer() && this.request == null) {
      //switch to agent detection
      this.logger.error('Request is null, unable to detect device type.');
    }
    else {
      this.logger.error('Device detection failed: Unable to determine runtime platform.');
    }

    if (!result.isMobile && !result.isDesktop && !result.isTablet) {
      this.logger.error('Device detection failed: No device type detected, defaulting to mobile.');
      result.isMobile = true;
    }

    result.isMobile = !( result.isTablet || result.isDesktop);
    result.isTablet = !(result.isMobile || result.isDesktop);
    result.isDesktop = !(result.isMobile || result.isTablet);

    return result;
  }

  constructor(@Inject(REQUEST) private request: Request, private ssrService: SsrService, @Optional() private transferState: TransferState, private appRef: ApplicationRef, private loggingService: LogginService) {

    this.logger = this.loggingService.logger.child({
      source: 'device-detection-service'
    });
  }
}